import { type BaseButtonProps, Button } from "@msuite/ui/button";
import type { Disclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import type { NotesTable } from "@msuite/utils/types";
import { useNoteCapabilities } from "@/hooks/use-note-capabilities";
import { useNotes } from "@/hooks/use-notes";

/** Props Interface */
export interface NoteHintButtonProps extends BaseButtonProps {
	disclosure: Disclosure;
	notesTable: NotesTable;
	identifier: string;
}

export function NoteHintButton({
	disclosure,
	notesTable,
	identifier,
	variant,
}: NoteHintButtonProps) {
	/** Hooks */
	const { t } = useTranslation();
	const noteCapabilities = useNoteCapabilities();
	const canReadParent = noteCapabilities.canReadParent(notesTable);

	/** Query */
	// The count is a read of the parent's notes like any other, so it waits for the same answer —
	// otherwise the button asks on every render of a surface its caller may not open.
	const { notes } = useNotes(notesTable, identifier, { enabled: canReadParent });

	/** Guard */
	if (!canReadParent) return null;

	/** Render */
	return (
		<Button
			icon={
				<Icon
					name="MessagesSquare"
					className={cn(notes.length > 0 ? "stroke-accent-500" : "stroke-primary-500")}
				/>
			}
			onClick={disclosure.onToggle}
			variant={variant || "plain"}
		>
			{notes.length} {t("notes.notes")}
		</Button>
	);
}
